import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { PlayerVideo } from '@/components/video/PlayerVideo.js';

type AulaDetail = {
  id: string;
  tipo?: string;
  titulo: string;
  descricao?: string | null;
  conteudo_url?: string | null;
  video_id?: string | null;
  trilha_slug: string;
  trilha_titulo: string;
  programa_titulo: string;
  modulo_titulo: string;
};

type Progresso = {
  progresso_pct: number;
  concluida: boolean;
  concluida_em: string | null;
};

export function AulaPlayerPage() {
  const { aulaId } = useParams<{ aulaId: string }>();
  const qc = useQueryClient();
  const [localPct, setLocalPct] = useState(0);

  const aula = useQuery({
    queryKey: ['aula', aulaId],
    enabled: !!aulaId,
    queryFn: async () => (await api.get<AulaDetail>(`/conteudo/aulas/${aulaId}`)).data,
  });

  const videoId = aula.data?.video_id ?? null;

  const progresso = useQuery({
    queryKey: ['aula-progresso', aulaId],
    enabled: !!aulaId,
    // Em aula com vídeo quem escreve o progresso é a medição do player; a
    // tela só relê de tempos em tempos para a barra acompanhar.
    refetchInterval: videoId ? 15_000 : false,
    queryFn: async () =>
      (await api.get<Progresso>(`/conteudo/aulas/${aulaId}/progresso`)).data,
  });

  useEffect(() => {
    if (progresso.data) setLocalPct(progresso.data.progresso_pct);
  }, [progresso.data?.progresso_pct]);

  const playback = useQuery({
    queryKey: ['aula-playback', aulaId, videoId],
    enabled: !!aulaId && !!videoId,
    retry: false,
    queryFn: async () =>
      (await api.get<{ codigo: string; situacao: string }>(`/conteudo/aulas/${aulaId}/playback`)).data,
  });

  const saveProgress = useMutation({
    mutationFn: async (payload: { progresso_pct: number; concluida?: boolean }) =>
      api.post(`/conteudo/aulas/${aulaId}/progresso`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aula-progresso', aulaId] });
    },
  });

  if (aula.isLoading) {
    return <PageLayout title="Aula">Carregando…</PageLayout>;
  }
  if (aula.isError || !aula.data) {
    return (
      <PageLayout title="Aula não encontrada">
        <Link to="/aprendizado" className="text-sm text-accent underline">
          ← Voltar ao aprendizado
        </Link>
      </PageLayout>
    );
  }

  const d = aula.data;
  const concluida = progresso.data?.concluida ?? false;

  return (
    <PageLayout
      title={d.titulo}
      description={`${d.trilha_titulo} · ${d.programa_titulo} · ${d.modulo_titulo}`}
    >
      <Link
        to={`/aprendizado/trilha/${d.trilha_slug}`}
        className="mb-4 inline-block text-xs text-ha-muted underline hover:text-ha-text"
      >
        ← {d.trilha_titulo}
      </Link>

      {localPct > 0 && !concluida && (
        <p className="mb-3 text-xs text-accent">
          Continuar de onde parou — progresso salvo: {localPct}%
        </p>
      )}

      {d.descricao && <p className="mb-6 text-sm text-ha-subtle">{d.descricao}</p>}

      {d.tipo === 'TEXTO' && d.conteudo_url && (
        <div className="mb-6 rounded-card border border-ha-border2 bg-ha-card2 p-4 text-sm whitespace-pre-wrap">
          {d.conteudo_url.startsWith('http') ? (
            <a href={d.conteudo_url} className="text-accent underline" target="_blank" rel="noreferrer">
              Abrir conteúdo
            </a>
          ) : (
            d.conteudo_url
          )}
        </div>
      )}
      {d.tipo === 'PDF' && d.conteudo_url && (
        <a
          href={d.conteudo_url}
          target="_blank"
          rel="noreferrer"
          className="mb-6 inline-block text-sm font-bold text-accent underline"
        >
          Abrir PDF
        </a>
      )}
      {videoId && playback.data && (
        <div className="mb-4 aspect-video w-full overflow-hidden rounded-card border border-ha-border2 bg-black">
          <PlayerVideo key={playback.data.codigo} codigo={playback.data.codigo} aulaId={d.id} />
        </div>
      )}
      {videoId && playback.isError && (
        <div className="mb-4 flex aspect-video items-center justify-center rounded-card border border-ha-border2 bg-ha-card2 text-sm text-ha-muted">
          Vídeo indisponível no momento.
        </div>
      )}
      {!videoId && (d.tipo === 'VIDEO' || !d.tipo) && (
        <div className="mb-4 flex aspect-video items-center justify-center rounded-card border border-ha-border2 bg-ha-card2 text-sm text-ha-muted">
          Vídeo em breve.
        </div>
      )}

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-xs text-ha-subtle">
          <span>Progresso</span>
          <span>{localPct}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-ha-card2">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${Math.min(100, localPct)}%` }}
          />
        </div>
        {/* Sem vídeo não há o que medir: o aluno diz até onde foi. */}
        {!videoId && (
          <>
            <input
              type="range"
              min={0}
              max={100}
              value={localPct}
              className="mt-3 w-full accent-[#ffc800]"
              onChange={(e) => setLocalPct(Number(e.target.value))}
              onMouseUp={() =>
                saveProgress.mutate({ progresso_pct: localPct, concluida: localPct >= 100 })
              }
              onTouchEnd={() =>
                saveProgress.mutate({ progresso_pct: localPct, concluida: localPct >= 100 })
              }
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  className="rounded border border-ha-border2 px-2 py-1 text-[10px] font-bold uppercase hover:border-accent"
                  onClick={() => {
                    setLocalPct(pct);
                    saveProgress.mutate({ progresso_pct: pct, concluida: pct >= 100 });
                  }}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        className="rounded-element border border-ha-border2 bg-ha-card px-6 py-3 text-[10px] font-bold uppercase text-ha-text transition hover:border-accent disabled:opacity-50"
        onClick={() => saveProgress.mutate({ progresso_pct: 100, concluida: true })}
        disabled={saveProgress.isPending || concluida}
      >
        {concluida ? 'Concluída ✓' : 'Marcar como concluída'}
      </button>
    </PageLayout>
  );
}
